import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import SEO from '../components/SEO'
import {
  githubAffiliateProjectApi,
  type GitHubAffiliateProject as ApiGitHubAffiliateProject,
} from '../services/api'

interface GithubProject {
  slug: string
  owner: string
  repo: string
  name: string
  repoUrl: string
  branch?: string
}

const SEEDED_PROJECT_FALLBACKS: Record<string, GithubProject> = {
  'chatgptnextweb/nextchat': {
    slug: 'chatgptnextweb/nextchat',
    owner: 'ChatGPTNextWeb',
    repo: 'NextChat',
    name: 'NextChat',
    repoUrl: 'https://github.com/ChatGPTNextWeb/NextChat',
    branch: 'main',
  },
}

function normalizeSlug(owner = '', repo = '') {
  return `${owner}/${repo}`.toLowerCase()
}

function titleCaseRepo(repo: string) {
  return repo
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'GitHub Project'
}

function getProject(owner?: string, repo?: string): GithubProject {
  const slug = normalizeSlug(owner, repo)
  const seeded = SEEDED_PROJECT_FALLBACKS[slug]
  if (seeded) return seeded

  const fallbackOwner = owner || 'owner'
  const fallbackRepo = repo || 'repo'

  return {
    slug,
    owner: fallbackOwner,
    repo: fallbackRepo,
    name: titleCaseRepo(fallbackRepo),
    repoUrl: `https://github.com/${fallbackOwner}/${fallbackRepo}`,
  }
}

function projectFromApi(project: ApiGitHubAffiliateProject): GithubProject {
  return {
    slug: project.slug,
    owner: project.owner,
    repo: project.repo,
    name: project.display_name || titleCaseRepo(project.repo),
    repoUrl: project.repo_url,
    branch: project.branch,
  }
}

function buildPrompt(project: GithubProject) {
  return [
    'Review this GitHub project for a TokenDance FDE or LLM infrastructure engagement:',
    project.repoUrl,
  ].join('\n')
}

function fallbackCopy(text: string) {
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  document.body.appendChild(textArea)
  textArea.select()

  try {
    document.execCommand('copy')
  } finally {
    document.body.removeChild(textArea)
  }
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 2C6.48 2 2 6.59 2 12.25c0 4.52 2.86 8.35 6.84 9.7.5.09.68-.22.68-.49v-1.9c-2.78.62-3.37-1.22-3.37-1.22-.45-1.18-1.11-1.49-1.11-1.49-.91-.64.07-.63.07-.63 1 .07 1.53 1.06 1.53 1.06.89 1.56 2.34 1.11 2.91.85.09-.66.35-1.11.63-1.36-2.22-.26-4.56-1.14-4.56-5.06 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.71 0 0 .84-.28 2.75 1.05A9.37 9.37 0 0 1 12 6.96c.85 0 1.7.12 2.5.34 1.91-1.33 2.75-1.05 2.75-1.05.55 1.41.2 2.45.1 2.71.64.72 1.03 1.63 1.03 2.75 0 3.93-2.34 4.8-4.57 5.06.36.32.68.94.68 1.9v2.8c0 .27.18.59.69.49A10.14 10.14 0 0 0 22 12.25C22 6.59 17.52 2 12 2Z"
        fill="currentColor"
      />
    </svg>
  )
}

function BranchIcon() {
  return (
    <svg className="od-github-branch-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M7 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0 0v10a3 3 0 0 0 3 3h4m3-3a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0-8a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0 0v3a3 3 0 0 1-3 3h-2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

function getAgentLinks(prompt: string) {
  return [
    {
      id: 'claude',
      label: 'Open in Claude Code',
      icon: '/agent-icons/claude.svg',
      href: `claude://code/new?${new URLSearchParams({ q: prompt })}`,
    },
    {
      id: 'codex',
      label: 'Open in Codex',
      icon: '/agent-icons/chatgpt.svg',
      href: `codex://new?${new URLSearchParams({ prompt })}`,
    },
    {
      id: 'cursor',
      label: 'Open in Cursor',
      icon: '/agent-icons/cursor.svg',
      href: `https://cursor.com/link/prompt?${new URLSearchParams({ text: prompt })}`,
    },
    {
      id: 'openclaw',
      label: 'Copy prompt to OpenClaw',
      icon: '/agent-icons/openclaw.png',
    },
  ] as const
}

export default function GithubDeployProject() {
  const { owner, repo } = useParams()
  const fallbackProject = useMemo(() => getProject(owner, repo), [owner, repo])
  const [catalogProject, setCatalogProject] = useState<GithubProject | null>(null)
  const project = catalogProject?.slug === fallbackProject.slug ? catalogProject : fallbackProject
  const prompt = useMemo(() => buildPrompt(project), [project])
  const agentLinks = useMemo(() => getAgentLinks(prompt), [prompt])
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    document.body.classList.add('v2-github-project-page')
    return () => document.body.classList.remove('v2-github-project-page')
  }, [])

  useEffect(() => {
    let cancelled = false
    setCatalogProject(null)

    if (!owner || !repo) {
      return () => {
        cancelled = true
      }
    }

    githubAffiliateProjectApi.get(owner, repo)
      .then(({ project: apiProject }) => {
        if (!cancelled) setCatalogProject(projectFromApi(apiProject))
      })
      .catch(() => {
        if (!cancelled) setCatalogProject(null)
      })

    return () => {
      cancelled = true
    }
  }, [owner, repo])

  const copyPrompt = useCallback(() => {
    const markCopied = () => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(prompt).then(markCopied).catch(() => {
        fallbackCopy(prompt)
        markCopied()
      })
      return
    }

    fallbackCopy(prompt)
    markCopied()
  }, [prompt])

  const canonicalPath = `/github/${project.owner.toLowerCase()}/${project.repo.toLowerCase()}`
  const title = `Deploy ${project.name} with TokenDance`
  const description = `Copy one prompt, paste it into your coding agent, and deploy ${project.owner}/${project.repo} before signup or payment.`
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `https://tokendance.net${canonicalPath}#webpage`,
    url: `https://tokendance.net${canonicalPath}`,
    name: title,
    description,
    isPartOf: { '@id': 'https://tokendance.net/#website' },
    about: {
      '@type': 'SoftwareSourceCode',
      name: `${project.owner}/${project.repo}`,
      codeRepository: project.repoUrl,
    },
    inLanguage: 'en',
  }

  return (
    <>
      <SEO title={title} description={description} path={canonicalPath} jsonLd={jsonLd} />

      <div className="od-github-shell">
        <Link className="od-brand od-github-brand" aria-label="TokenDance home" to="/" data-discover="true">
          <span className="od-logo-mark" aria-hidden="true">
            <img src="/logo.png" alt="" decoding="async" />
          </span>
          <span>TokenDance</span>
        </Link>

        <main className="od-github-main">
          <section className="od-github-card" aria-label={`Deploy ${project.name}`}>
            <section className="od-github-section" aria-labelledby="github-repo-title">
              <h2 id="github-repo-title">GitHub repo</h2>
              <a className="od-github-repo-row" href={project.repoUrl} target="_blank" rel="noopener noreferrer">
                <GitHubIcon />
                <span className="od-github-repo-copy">
                  <small>Cloning from GitHub</small>
                  <strong>{project.owner}/{project.repo}</strong>
                </span>
                {project.branch ? (
                  <span className="od-github-branch">
                    <BranchIcon />
                    {project.branch}
                  </span>
                ) : null}
              </a>
            </section>

            <section className="od-github-section od-github-prompt-section" aria-labelledby="github-deploy-title">
              <div className="od-github-prompt-panel">
                <div className="od-github-prompt-intro">
                  <h2 id="github-deploy-title">Deploy using your agent</h2>
                  <p>Copy prompt. Paste into your agent. Agent deploys and returns URL.</p>
                </div>

                <div className="od-agent-open-list" aria-label="Open prompt in coding agent">
                  {agentLinks.map((agent) => {
                    const content = (
                      <>
                        <span className="od-agent-open-icon">
                          <img src={agent.icon} alt="" decoding="async" />
                        </span>
                        <span>{agent.label}</span>
                      </>
                    )

                    if (!('href' in agent)) {
                      return (
                        <button
                          key={agent.id}
                          type="button"
                          className={`od-agent-open-row od-agent-open-row-${agent.id}`}
                          onClick={copyPrompt}
                        >
                          {content}
                        </button>
                      )
                    }

                    return (
                      <a
                        key={agent.id}
                        className={`od-agent-open-row od-agent-open-row-${agent.id}`}
                        href={agent.href}
                        target={agent.href.startsWith('https://') ? '_blank' : undefined}
                        rel={agent.href.startsWith('https://') ? 'noopener noreferrer' : undefined}
                        onClick={copyPrompt}
                      >
                        {content}
                      </a>
                    )
                  })}
                </div>

                <div className="od-github-manual">
                  <span>or copy manually</span>
                </div>

                <button
                  type="button"
                  className="od-github-copy-primary"
                  onClick={copyPrompt}
                  aria-label="Copy GitHub deploy prompt"
                >
                  {copied ? 'Copied' : 'copy prompt to deploy project'}
                </button>

                <div className="od-github-prompt-box">
                  <span className="od-github-prompt-label">prompt</span>
                  <div
                    className="od-github-prompt-text"
                    aria-label="TokenDance GitHub deploy prompt"
                  >
                    {prompt}
                  </div>
                </div>
              </div>
            </section>

            <section className="od-github-section" aria-labelledby="github-next-title">
              <h2 id="github-next-title">What's next</h2>
              <ol className="od-github-next-list">
                <li>
                  <span>1</span>
                  <p>Share the repo with TokenDance so we can scope the production path.</p>
                </li>
                <li>
                  <span>2</span>
                  <p>We map the app, model calls, data boundaries, and infrastructure needs.</p>
                </li>
                <li>
                  <span>3</span>
                  <p>We build the smallest production slice and transfer the operating notes.</p>
                </li>
              </ol>
            </section>
          </section>
        </main>
      </div>
    </>
  )
}
